/* What kind of document a case file is (FR-45, UX-DR-14, migration 0045).
 *
 * The client's half of a vocabulary the server owns. The wire carries a CODE —
 * 'cmr', 'awb', 'customs-declaration' — and the label is composed here, in the
 * reader's language (AD-27): a server-rendered "Air waybill" would arrive in a
 * Russian file area in English, and worse, would make the GROUPING KEY a
 * translated string, so the same case would group differently for two
 * colleagues looking at it side by side.
 *
 * Why the type exists at all, given that `contentType` is already on the row:
 * `application/pdf` says how to render the bytes. It does not say whether the
 * PDF is a waybill, a declaration or a signed contract, and those are the only
 * distinctions this desk searches by. Forty PDFs grouped by "PDF" are forty
 * files grouped by nothing.
 */

/** The vocabulary, mirroring collab.DocKind and the `case_file_kind` domain.
 *
 *  Three declarations of one list — SQL enforces it, Go names it, this renders
 *  it — and none imports another. What keeps them in step is a test on each
 *  side rather than a shared module: `collab/dockind_test.go` reads the
 *  migration, and `fileKinds.test.ts` pins that every code here has a label in
 *  both locales. */
export const FILE_KINDS = [
  // The transport documents, one per mode. A leg already records their
  // NUMBERS (casefile.Leg.Reference); these record that a scan is filed here.
  'cmr',
  'awb',
  'rail-waybill',
  'bill-of-lading',

  // The commercial paper that travels with the cargo.
  'invoice',
  'packing-list',
  'customs-declaration',
  'certificate',

  // What the deal itself is written on.
  'contract',

  // The reload photograph from a bonded warehouse: the commonest upload this
  // product has, and not a document at all.
  'photo',

  // The default, and deliberately reachable — see the upload sheet.
  'other',
] as const

export type FileKind = (typeof FILE_KINDS)[number]

/** What an upload that declared nothing is filed as, and what an unrecognised
 *  code renders as.
 *
 *  The server normalises the same way, so the two agree without either
 *  trusting the other: a row can only ever hold a code from this list, and a
 *  row from a NEWER build than this tab still renders rather than blanking. */
export const DEFAULT_FILE_KIND: FileKind = 'other'

/** Narrows a code off the wire, falling back rather than throwing.
 *
 *  A file area that threw on an unknown code would be a screen taken down by a
 *  deploy that added a kind — the exact reverse of what an escape hatch is
 *  for. */
export function asFileKind(raw: string | undefined): FileKind {
  const found = FILE_KINDS.find((kind) => kind === raw)
  return found ?? DEFAULT_FILE_KIND
}

/** The i18n key for a kind's label.
 *
 *  Built here rather than at each call site so that the key shape lives in one
 *  place: `cases.files.kinds.<code>`. The cast is the price of an interpolated
 *  key — every code in FILE_KINDS has an entry, which fileKinds.test.ts is what
 *  actually guarantees. */
export function fileKindLabelKey(kind: FileKind): 'cases.files.kinds.other' {
  return `cases.files.kinds.${kind}` as 'cases.files.kinds.other'
}
